import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DateField } from "@/components/ui/date-field";

export const STAGES = ["draft", "sent", "approved", "in_production", "waiting_shipment", "shipped", "paid", "declined"];
export const LABELS = { draft: "Draft", sent: "Sent", approved: "Approved", in_production: "In production",
  waiting_shipment: "Waiting for shipment", shipped: "Shipped", paid: "Paid", declined: "Declined" };
const TONE = { draft: "border-slate-700 text-slate-400", sent: "border-sky-500/50 text-sky-700", approved: "border-amber-500/60 text-amber-600",
  in_production: "border-violet-400/50 text-violet-700", waiting_shipment: "border-orange-400/50 text-orange-700",
  shipped: "border-teal-500/50 text-teal-700", paid: "border-emerald-500/50 text-emerald-700", declined: "border-red-400/50 text-red-600" };
const INV_TONE = { paid: "border-emerald-500/50 text-emerald-700", partial: "border-amber-500/60 text-amber-600", unpaid: "border-red-400/50 text-red-600",
  not_due: "border-slate-700 text-slate-400", void: "border-slate-700 text-slate-500 line-through" };
export const INV_LABEL = { paid: "Paid", partial: "Partial", unpaid: "Unpaid", not_due: "Not yet due", void: "Void" };

export function StageBadge({ stage, testId }) {
  return <span data-testid={testId} className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${TONE[stage] || TONE.draft}`}>{LABELS[stage] || stage}</span>;
}

export function InvoiceBadge({ inv, testId }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span data-testid={testId} className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${INV_TONE[inv.status]}`}>{INV_LABEL[inv.status]}</span>
      {inv.overdue && <span className="rounded-full bg-red-500/10 px-2 py-0.5 text-[10px] font-bold uppercase text-red-600">Overdue</span>}
    </span>
  );
}

export const inp = "h-10 w-full rounded-lg border border-slate-800 bg-white px-3 text-sm text-slate-100 outline-none transition-colors focus:border-amber-500/70";
export const inpI = inp.replace("w-full ", "");
export const btnGold = "inline-flex h-10 items-center justify-center gap-2 rounded-full bg-amber-500 px-5 text-sm font-semibold text-white transition-colors hover:bg-amber-600 disabled:opacity-50";
export const btnLine = "inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-slate-700 px-4 text-xs font-semibold text-slate-200 transition-colors hover:border-amber-500/60 hover:text-amber-600 disabled:opacity-50";
export const days = (n) => (n == null ? "—" : `${n} day${n === 1 ? "" : "s"}`);
export const fmtAt = (s) => (s ? new Date(s).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "");

export function usePeople() {
  const [people, setPeople] = useState({ me: {}, showrooms: [], users: [] });
  useEffect(() => { api.get("/crm/people").then((r) => setPeople(r.data)).catch(() => {}); }, []);
  return people;
}

export function ScopeFilters({ people, f, setF, prefix, stages = false }) {
  const role = people.me.role;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${prefix}-filters`}>
      <input data-testid={`${prefix}-search`} value={f.q || ""} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search…" className={`${inpI} w-56`} />
      {role === "admin" && (
        <select data-testid={`${prefix}-showroom-filter`} value={f.showroom_id || ""} onChange={(e) => setF({ ...f, showroom_id: e.target.value })} className={`${inpI} w-44`}>
          <option value="">All showrooms</option>
          {people.showrooms.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      )}
      {role !== "rep" && (
        <select data-testid={`${prefix}-rep-filter`} value={f.rep_id || ""} onChange={(e) => setF({ ...f, rep_id: e.target.value })} className={`${inpI} w-44`}>
          <option value="">All reps</option>
          {people.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      )}
      {stages && (
        <select data-testid={`${prefix}-stage-filter`} value={f.stage || ""} onChange={(e) => setF({ ...f, stage: e.target.value })} className={`${inpI} w-44`}>
          <option value="">All stages</option>
          {STAGES.map((s) => <option key={s} value={s}>{LABELS[s]}</option>)}
        </select>
      )}
      <DateField testId={`${prefix}-date-from`} value={f.date_from || ""} onChange={(v) => setF({ ...f, date_from: v })} placeholder="From" clearable className={`${inpI} w-40`} />
      <DateField testId={`${prefix}-date-to`} value={f.date_to || ""} onChange={(v) => setF({ ...f, date_to: v })} placeholder="To" clearable className={`${inpI} w-40`} />
    </div>
  );
}

export const qs = (f) => new URLSearchParams(Object.entries(f).filter(([, v]) => v !== "" && v != null)).toString();

export function PageHead({ kicker, title, sub, right }) {
  return (
    <div className="rise flex flex-wrap items-end justify-between gap-4">
      <div>
        {kicker && <p className="text-xs font-semibold uppercase tracking-[0.3em] text-amber-500">{kicker}</p>}
        <h1 className="font-display text-4xl font-semibold tracking-tight text-slate-50 sm:text-5xl">{title}</h1>
        {sub && <p className="mt-3 max-w-2xl text-sm text-slate-400 md:text-base">{sub}</p>}
      </div>
      {right}
    </div>
  );
}
